// SalesIntroCallResultScreen — a Sales Mesterműhely · Záró beszélgetés
// script dedikált eredménykomponense (15 fázisos LINE/NOTE renderer).
//
// PIXELPONTOS KLÓN a LandingSalesResultScreen.js-ből: azonos külső
// megjelenés (dark shell → krém wrapper → fehér doc), azonos felső
// akciósáv (Másolás / Nyelvi tisztítás / Letöltés / Mentés a Tudástárba),
// azonos bal oldali finomítópanel (kijelölés → quick actions → SuggestionCard
// → Alkalmazom/Új verzió/Mégsem), azonos F3-mintás refine state machine.
//
// Eltérések a Landing Sales-től:
//   • A `page` state → `script` state, `{call_type, phases[]}` sémával.
//   • A 14 SalesXxxSection komponens helyett EGY közös `PhaseBlock`
//     komponens, ami 15 fázist rendel LINE / NOTE blokkal.
//   • Backend endpointok: `/sales-intro-call/script/*` (nem
//     `/conversion/landing-sales/page/*`).
//   • Nincs kereszt-agent nav gomb.
//   • PostHeader címek: "Sales Mesterműhely · Záró beszélgetés" /
//     tab2 label "Záró beszélgetés".
//
// A Landing Sales flow fájljait NEM módosítjuk — technikailag különálló.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Copy, DownloadSimple, BookmarkSimple, PencilSimple,
  ArrowsCounterClockwise, CircleNotch, CheckCircle,
  PaperPlaneTilt, Paperclip, X as XIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getActiveAlap } from "../lib/activeAlap";
import { PostHeader } from "./MagnetPostFacebook";
import { SuggestionCard, AppliedBlock } from "../components/AgentOutputScreen";

const GOLD_DEEP = "#8a6206";
const GOLD_LABEL = "#b5850a";
const GOLD_PRIMARY = "#eab308";
const CREAM_WORK = "#f4ede2";
const CARD_BORDER = "#ece2c8";
const TEXT_DARK = "#221f19";
const TEXT_MID = "#4b4438";
const TEXT_BODY = "#3a3428";
const TEXT_SECOND = "#5c5648";
const DIVIDER_START = "#f3d98a";
const DIVIDER_END = "#f9f2dd";

const GOLD_BG = "rgba(255,219,92,0.14)";
const GOLD_BORDER = "rgba(255,219,92,0.36)";

// Záró beszélgetés kontextushoz igazított 6-elemű quick-refine készlet.
// Ugyanaz a UI-minta, csak a `instruction` szövege más — nyilvánvaló okból
// a coach-hívás kontextusához illik.
const QUICK_REFINE_ACTIONS = [
  { id: "shorten",   label: "Rövidebb",     instruction: "Rövidítsd le a mondatot 1–2 tömör mondatra. Ne veszíts el konkrét tartalmat, csak töröld a felesleget." },
  { id: "human",     label: "Élőbb",        instruction: "Természetesebb magyar élőbeszéd, ahogy egy tapasztalt szakértő hangosan mondaná — nem előadói, nem AI-szagú." },
  { id: "soft",      label: "Lágyabb",      instruction: "Melegebb, empatikusabb, barátságosabb hangnem, több megértés." },
  { id: "clarify",   label: "Egyértelműbb", instruction: "Fogalmazd egyértelműbbre — kevesebb ami/amely/hogy szerkezet, tisztább jelentés." },
  { id: "question",  label: "Kérdés-forma", instruction: "Ha ez egy kimondható LINE, alakítsd nyitott kérdéssé — vagy kombinálj hozzá egy visszakérdezést." },
  { id: "note",      label: "Nyugodtabb",   instruction: "Tegyél bele egy csendességet vagy türelmet közvetítő elemet — várd meg az ügyfél válaszát." },
];

/* ─── script ↔ text helpers ─────────────────────────────────────── */

function _replaceInScriptDeep(node, quote, snippet, state) {
  if (state.done) return node;
  if (typeof node === "string") {
    const idx = node.indexOf(quote);
    if (idx !== -1) {
      state.done = true;
      return node.slice(0, idx) + snippet + node.slice(idx + quote.length);
    }
    return node;
  }
  if (Array.isArray(node)) {
    return node.map((child) => _replaceInScriptDeep(child, quote, snippet, state));
  }
  if (node && typeof node === "object") {
    const next = {};
    for (const k of Object.keys(node)) {
      next[k] = _replaceInScriptDeep(node[k], quote, snippet, state);
    }
    return next;
  }
  return node;
}
function applyQuoteReplacement(script, quote, snippet) {
  const state = { done: false };
  const nextScript = _replaceInScriptDeep(script, quote, snippet, state);
  return { script: nextScript, changed: state.done };
}

function scriptToPlainText(script) {
  if (!script) return "";
  const lines = [];
  lines.push("SALES MESTERMŰHELY · 10 PERCES TELEFON SCRIPT");
  lines.push("═".repeat(50));
  lines.push("");
  const rawPhases = Array.isArray(script.phases) ? script.phases : [];
  const phases = rawPhases;
  for (const ph of phases) {
    const num = String(ph.number ?? "").padStart(2, "0");
    const header = `${num} · ${ph.title || ""}${ph.duration ? ` · ${ph.duration}` : ""}`;
    lines.push(header);
    lines.push("─".repeat(header.length));
    for (const b of (ph.blocks || [])) {
      if (b.type === "line") {
        // A **bold** és [placeholder] markereket megőrizzük — a plain
        // text export hű másolatot ad, a coach papíron is látja őket.
        lines.push(b.text || "");
      } else if (b.type === "note") {
        const raw = String(b.text || "");
        const peldaMatch = /^\s*PÉLDA\s*:\s*/i.exec(raw);
        lines.push("");
        if (peldaMatch) {
          lines.push("[PÉLDA]");
          lines.push(raw.slice(peldaMatch[0].length));
        } else {
          lines.push("[INSTRUKCIÓ]");
          lines.push(raw);
        }
      }
      lines.push("");
    }
    lines.push("");
  }
  return lines.join("\n").trim() + "\n";
}

/* ═══════════════════════════════════════════════════════════════════════
   FŐ KOMPONENS
   ═══════════════════════════════════════════════════════════════════════ */

export default function SalesIntroCallResultScreen({
  script: initialScript, inputs, onBack, onExit,
}) {
  const [script, setScript] = useState(initialScript);
  const [savedName, setSavedName] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // ── F3-mintás refine state machine (WebinarSectionCarousel-lel identikus) ──
  const [chatInput, setChatInput] = useState("");
  const [refinePhase, setRefinePhase] = useState("idle"); // idle | loading | suggestion | applied
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionError, setSuggestionError] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [selection, setSelection] = useState(null);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const undoTimerRef = useRef(null);
  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);

  useEffect(() => { setScript(initialScript); }, [initialScript]);

  // ── Szövegkijelölés figyelése a fehér dokumentumkártyán belül ──
  useEffect(() => {
    const handler = () => {
      try {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const text = String(sel.toString() || "").trim();
        if (!text || text.length < 3) return;
        const anchor = sel.anchorNode?.parentElement?.closest?.('[data-testid="sales-intro-call-result-doc"]');
        const focus = sel.focusNode?.parentElement?.closest?.('[data-testid="sales-intro-call-result-doc"]');
        if (anchor && focus) setSelection(text);
      } catch { /* noop */ }
    };
    document.addEventListener("mouseup", handler);
    document.addEventListener("keyup", handler);
    return () => {
      document.removeEventListener("mouseup", handler);
      document.removeEventListener("keyup", handler);
    };
  }, []);

  useEffect(() => () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, []);

  const updatePhase = (phaseIdx, newPhase) =>
    setScript((s) => {
      const nextPhases = (s?.phases || []).map((p, i) => (i === phaseIdx ? newPhase : p));
      return { ...s, phases: nextPhases };
    });

  const clearSelection = () => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  const copyAll = async () => {
    const text = scriptToPlainText(script);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Teljes script vágólapra másolva");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Nem sikerült a másolás"); }
  };
  const downloadAll = () => {
    const text = scriptToPlainText(script);
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `10-perces-telefon-script-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Letöltés elindítva");
  };
  const saveToVault = async () => {
    if (saving) return;
    const name = window.prompt("Milyen néven mentsük a Tudástárba?", "10 perces telefon script");
    if (!name || !name.trim()) return;
    setSaving(true);
    try {
      const res = await api.post("/sales-intro-call/script/save", {
        name: name.trim(), script, inputs: inputs || null,
      });
      if (res.data?.ok) {
        setSavedName(name.trim());
        toast.success("Mentve a Tudástárba");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mentés sikertelen");
    } finally { setSaving(false); }
  };

  // ── Egyszeri magyar nyelvi tisztítás — ELTÁVOLÍTVA a felületről.
  // A nyelvi ellenőrzés a Writer kötelező belső lépése (`_writer_sales_call_closing_script_v1.md`
  // → "KÖTELEZŐ MAGYAR NYELVI ELLENŐRZÉS" szakasz), nem külön felhasználói művelet.
  // A backend proofread endpoint továbbra is elérhető, de az UI nem hívja.

  /* ─── F3-mintás refine adapter ─── */

  const _callRefine = async (utasitas, quoteVal) =>
    api.post("/sales-intro-call/script/refine", {
      hivas_kontextus:      inputs?.hivas_kontextus      || "",
      fit_kovetkezo_lepes:  inputs?.fit_kovetkezo_lepes  || "",
      nev:                  inputs?.nev                  || "",
      szerep:               inputs?.szerep               || "",
      szerep_egyeb:         inputs?.szerep_egyeb         || "",
      current_script: script,
      user_instruction: utasitas || "",
      quote: quoteVal || null,
    });

  const requestRefine = async (instructionOverride, quickButtonId = null) => {
    const utasitas = ((instructionOverride && typeof instructionOverride === "string" ? instructionOverride : "") || chatInput).trim();
    if (!utasitas && !quickButtonId) return;
    if (refinePhase === "loading" || refinePhase === "suggestion") return;
    const quoteToSend = selection;
    const finalInstr = quickButtonId
      ? (QUICK_REFINE_ACTIONS.find((q) => q.id === quickButtonId)?.instruction || utasitas)
      : utasitas;
    setSuggestionError(false);
    setRefinePhase("loading");
    if (!instructionOverride && !quickButtonId) setChatInput("");
    try {
      const res = await _callRefine(finalInstr, quoteToSend);
      const data = res.data || {};
      setSuggestion({
        mod: "teljes", // A backend a teljes scriptet küldi vissza refine-nél
        snippet: "",
        teljesScript: data.script || null,
        instr: finalInstr,
        quote: quoteToSend || null,
      });
      setRefinePhase("suggestion");
    } catch (e) {
      setSuggestion({ mod: "error", instr: finalInstr, quote: quoteToSend || null });
      setSuggestionError(true);
      setRefinePhase("suggestion");
    }
  };

  const retryVariant = async () => {
    if (!suggestion || regenerating) return;
    setRegenerating(true);
    setSuggestionError(false);
    try {
      const res = await _callRefine(
        (suggestion.instr || "") + "\n\nGenerálj egy érdemben MÁSIK változatot, mint az előzőt. Kerüld az ismétlést.",
        suggestion.quote,
      );
      const data = res.data || {};
      setSuggestion((s) => ({
        ...s,
        teljesScript: data.script || null,
        mod: "teljes",
      }));
    } catch (e) {
      setSuggestionError(true);
    } finally {
      setRegenerating(false);
    }
  };

  const retryAfterError = () => {
    if (!suggestion) return;
    setSuggestionError(false);
    requestRefine(suggestion.instr);
  };

  const cancelSuggestion = () => {
    setSuggestion(null);
    setSuggestionError(false);
    setRefinePhase("idle");
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    const snapshot = JSON.parse(JSON.stringify(script));
    if (suggestion.quote && suggestion.teljesScript) {
      // A backend teljes scriptet ad vissza. Ha volt kijelölés, dolgozunk a
      // teljes eredményből — a Writer refine promptja utasítást kapott, hogy
      // csak a kijelölt részt írja át. A `_replaceInScriptDeep` fallback nem
      // szükséges, mert a backend a teljes, javított scriptet küldte.
      setUndoSnapshot(snapshot);
      setScript(suggestion.teljesScript);
    } else if (suggestion.teljesScript) {
      setUndoSnapshot(snapshot);
      setScript(suggestion.teljesScript);
    } else {
      return;
    }
    setSelection(null);
    window.getSelection()?.removeAllRanges();
    setTimeout(() => { try { window.getSelection()?.removeAllRanges(); } catch { /* noop */ } }, 0);
    setSuggestion(null);
    setRefinePhase("applied");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setRefinePhase("idle");
      setUndoSnapshot(null);
    }, 8000);
  };

  const undoApplied = () => {
    if (!undoSnapshot) return;
    setScript(undoSnapshot);
    setUndoSnapshot(null);
    setRefinePhase("idle");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  };

  const phases = Array.isArray(script?.phases) ? script.phases : [];

  // ── Script főcím determinisztikus előállítása ───────────────────
  // A `[PROGRAM NEVE] – Záró beszélgetés script` cím a getActiveAlap()
  // offer-ből olvassa a program nevét. Ha nincs elérhető név, a cím a
  // "Záró beszélgetés script" alapra esik vissza. A JSON-séma NEM változik.
  const docTitle = useMemo(() => {
    try {
      const alap = getActiveAlap();
      const raw = alap?.offer?.name || alap?.offer?.title || "";
      const name = String(raw || "").trim();
      return name ? `${name} – 10 perces telefon script` : "10 perces telefon script";
    } catch { return "10 perces telefon script"; }
  }, []);

  return (
    <div
      className="flex flex-col"
      style={{ height: "100vh", background: "#0a0a12", color: "#e8e8f0", overflow: "hidden" }}
      data-testid="sales-intro-call-result-shell"
    >
      {/* Felső agentsáv — pixelpontosan a Landing Sales PostHeader-je,
          csak a headerTitle és tab2Label cserélve. */}
      <PostHeader
        onBackToOpening={onBack}
        activeTab="tab2"
        onTabChange={(t) => { if (t === "tab1") onBack?.(); }}
        tab2Disabled={false}
        headerTitle="Sales Mesterműhely · 10 perces telefon"
        tab2Label="10 perces telefon"
      />

      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">
        {/* ─── BAL — Finomítópanel (pixelpontosan a Landing Sales) ─── */}
        <div
          className="flex flex-col border-r min-h-0 h-full"
          style={{ borderColor: "rgba(255,255,255,0.04)" }}
          data-testid="sales-intro-call-result-finomito"
        >
          <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
            <div style={{ maxWidth: 480 }}>
              <div
                className="font-heading"
                style={{
                  fontSize: 28, fontWeight: 600, lineHeight: 1.3,
                  letterSpacing: "-0.015em", color: "#ffffff",
                  marginBottom: 18,
                }}
              >
                Elkészült a 10 perces telefon scripted.
              </div>
              <div style={{
                fontSize: 16, fontWeight: 400, lineHeight: 1.7, color: "#c0c0d0",
              }}>
                Olvasd át jobboldalt fázisról fázisra a scriptet. Ha valamit
                finomítanál, jelölj ki egy részt, és írd lent, mit változtassak.
                Ha minden rendben, mentsd el a Tudástárba.
              </div>
            </div>

            {/* Selection workspace */}
            <AnimatePresence>
              {(selection || refinePhase === "loading" || (refinePhase === "suggestion" && suggestion)) && (
                <motion.div
                  key="workspace"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    background: "rgba(124,108,242,0.025)",
                    border: "1px solid rgba(124,108,242,0.10)",
                    borderRadius: 12, padding: 14, margin: "32px -8px 0",
                  }}
                  data-testid="sales-intro-call-result-selection-workspace"
                >
                  {(selection || (suggestion && suggestion.quote)) && (
                    <div style={{
                      background: "rgba(255,255,255,0.04)",
                      borderLeft: "2px solid rgba(124,108,242,0.40)",
                      borderRadius: 8, padding: "11px 13px", marginBottom: 12,
                    }}>
                      <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0">
                          <div style={{
                            fontSize: 9, textTransform: "uppercase", letterSpacing: "0.16em",
                            color: "#8a8a9c", fontWeight: 600, marginBottom: 4,
                          }}>KIJELÖLT RÉSZ</div>
                          <div style={{
                            fontSize: 12.5, color: "#c0c0d0", lineHeight: 1.5,
                            display: "-webkit-box", WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical", overflow: "hidden",
                          }}>
                            {(() => {
                              const q = selection || suggestion?.quote || "";
                              return `„${q.length > 240 ? q.slice(0, 240) + "…" : q}"`;
                            })()}
                          </div>
                        </div>
                        {!(refinePhase === "suggestion" && suggestion) && refinePhase !== "loading" && (
                          <button
                            type="button"
                            onClick={clearSelection}
                            aria-label="Kijelölés törlése"
                            style={{
                              width: 22, height: 22, borderRadius: 5,
                              background: "rgba(255,255,255,0.04)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              color: "#9898ad", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}
                            data-testid="sales-intro-call-result-selection-clear"
                          >
                            <XIcon size={11} weight="bold" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {refinePhase !== "loading" && !(refinePhase === "suggestion" && suggestion) && selection && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="sales-intro-call-result-quick-actions">
                      {QUICK_REFINE_ACTIONS.map((qa) => (
                        <button
                          key={qa.id}
                          type="button"
                          onClick={() => requestRefine(qa.instruction, qa.id)}
                          data-testid={`sales-intro-call-result-quick-${qa.id}`}
                          style={{
                            fontSize: 13, padding: "10px 14px", borderRadius: 999,
                            background: "rgba(124,108,242,0.10)",
                            border: "1px solid rgba(124,108,242,0.28)",
                            color: "#cfc8ff", cursor: "pointer",
                            fontFamily: "inherit", fontWeight: 500,
                            whiteSpace: "nowrap", textAlign: "center", width: "100%",
                          }}
                        >{qa.label}</button>
                      ))}
                    </div>
                  )}

                  {refinePhase === "loading" && (
                    <div
                      className="flex items-center gap-2.5"
                      style={{ fontSize: 13, color: "#a8a8c0", padding: "4px 2px" }}
                      data-testid="sales-intro-call-result-refine-loading"
                    >
                      <CircleNotch size={14} weight="bold" className="animate-spin" />
                      Finomítás folyamatban…
                    </div>
                  )}

                  {refinePhase === "suggestion" && suggestion && (
                    <div data-testid="sales-intro-call-result-suggestion">
                      <SuggestionCard
                        snippet={
                          suggestion.teljesScript
                            ? scriptToPlainText(suggestion.teljesScript)
                            : ""
                        }
                        isError={suggestionError}
                        regenerating={regenerating}
                        onApply={applySuggestion}
                        onRetryVariant={retryVariant}
                        onRetryAfterError={retryAfterError}
                        onCancel={cancelSuggestion}
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {refinePhase === "applied" && (
                <motion.div
                  key="applied"
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ margin: "24px 0 0" }}
                  data-testid="sales-intro-call-result-applied"
                >
                  <AppliedBlock onUndo={undoApplied} canUndo={!!undoSnapshot} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const inputDisabled = refinePhase === "loading" || (refinePhase === "suggestion" && suggestion);
              if (chatInput.trim() && !inputDisabled) requestRefine();
            }}
            style={{ padding: "12px 24px 24px" }}
            data-testid="sales-intro-call-result-finomito-form"
          >
            {(() => {
              const isLoading = refinePhase === "loading";
              const showSuggestion = refinePhase === "suggestion" && suggestion;
              const inputDisabled = isLoading || showSuggestion;
              const placeholder = selection
                ? "Mit változtassak a kijelölt részen?"
                : "Jelölj ki egy részt jobboldalt, és írd ide, mit változtassak.";
              return (
                <>
                  <div style={{
                    background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
                    border: "1px solid rgba(243,238,228,0.14)",
                    borderRadius: 12,
                    padding: "5px 5px 5px 14px",
                    display: "flex", alignItems: "center", gap: 10,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
                    opacity: inputDisabled ? 0.5 : 1,
                    pointerEvents: inputDisabled ? "none" : "auto",
                  }}>
                    <button
                      type="button"
                      aria-label="Csatolás"
                      style={{
                        background: "transparent", border: "none", padding: 0,
                        color: "#8a8a9c", cursor: "pointer",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <Paperclip size={14} weight="regular" />
                    </button>
                    <input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          if (chatInput.trim() && !inputDisabled) requestRefine();
                        }
                      }}
                      placeholder={placeholder}
                      data-testid="sales-intro-call-result-chat-input"
                      disabled={inputDisabled}
                      className="flex-1 outline-none"
                      style={{
                        background: "transparent", border: "none", color: "white",
                        fontSize: 14, padding: "8px 0", fontFamily: "inherit",
                      }}
                    />
                    <button
                      type="submit"
                      disabled={!chatInput.trim() || inputDisabled}
                      data-testid="sales-intro-call-result-chat-send"
                      aria-label="Küldés"
                      style={{
                        width: 36, height: 36,
                        background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                        color: "white", borderRadius: 9, border: "none",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: chatInput.trim() && !inputDisabled ? "pointer" : "not-allowed",
                        opacity: chatInput.trim() && !inputDisabled ? 1 : 0.5,
                        boxShadow: chatInput.trim() && !inputDisabled
                          ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                          : "none",
                      }}
                    >
                      {isLoading
                        ? <CircleNotch size={15} weight="bold" className="animate-spin" />
                        : <PaperPlaneTilt size={15} weight="fill" />}
                    </button>
                  </div>
                  <div className="flex justify-between" style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}>
                    <span>Tipp: jelölj ki egy részt jobboldalt — a kérésed arra fog vonatkozni.</span>
                    <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
                  </div>
                </>
              );
            })()}
          </form>
        </div>

        {/* ─── JOBB — Eredményterület (krém wrapper: 4 gomb + fehér doc) ── */}
        <div className="h-full overflow-y-auto min-w-0" style={{ background: "#0a0a12" }}>
          <div style={{ padding: "26px" }}>
            <div
              style={{ background: CREAM_WORK, borderRadius: 16, padding: "22px 26px 26px" }}
              data-testid="sales-intro-call-result-cream"
            >
              {/* Felső akciósáv — 3 arany pill: Másolás / Letöltés / Mentés a Tudástárba. */}
              <div
                className="flex justify-end items-center flex-wrap"
                style={{ gap: 6, marginBottom: 20 }}
                data-testid="sales-intro-call-result-actions-bar"
              >
                <CreamActionPill
                  onClick={copyAll}
                  icon={copied ? CheckCircle : Copy}
                  label={copied ? "Másolva" : "Másolás"}
                  testid="sales-intro-call-result-copy-all"
                  active={copied}
                />
                <CreamActionPill
                  onClick={downloadAll}
                  icon={DownloadSimple}
                  label="Letöltés"
                  testid="sales-intro-call-result-download"
                />
                <CreamActionPill
                  onClick={saveToVault}
                  icon={savedName ? CheckCircle : BookmarkSimple}
                  label={saving ? "Mentés…" : savedName ? "Mentve" : "Mentés a Tudástárba"}
                  testid="sales-intro-call-result-save"
                  disabled={saving}
                  active={!!savedName}
                />
              </div>

              <motion.article
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }}
                style={{
                  background: "#ffffff", borderRadius: 20,
                  border: `0.5px solid ${CARD_BORDER}`,
                  boxShadow: "0 1px 3px rgba(60,50,20,0.05)",
                  padding: "56px 64px", width: "100%",
                }}
                className="sales-intro-call-result-doc"
                data-testid="sales-intro-call-result-doc"
              >
                {/* Script főcím — a Landing Sales Hero-cím stílusát követi
                    (font-heading, 500 weight, ~32px, letterSpacing -0.018em).
                    Determinisztikusan renderel — nem érinti a JSON-sémát. */}
                <header style={{ marginBottom: 44 }} data-testid="sales-intro-call-result-title">
                  <h1
                    className="font-heading"
                    style={{
                      fontSize: 32, fontWeight: 500, color: TEXT_DARK,
                      lineHeight: 1.2, letterSpacing: "-0.018em",
                      textAlign: "center", margin: 0,
                    }}
                  >
                    {docTitle}
                  </h1>
                </header>

                {phases.map((ph, idx) => (
                  <React.Fragment key={ph.id || idx}>
                    {idx > 0 && <Divider />}
                    <PhaseBlock
                      phase={ph}
                      onChange={(nv) => updatePhase(idx, nv)}
                    />
                  </React.Fragment>
                ))}
              </motion.article>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   CreamActionPill — PIXELPONTOSAN a Landing Sales-ből (arany háttér, arany
   keret, 999 border-radius, 12/500 tipó, 6px gap, spinning support).
   ═══════════════════════════════════════════════════════════════════════ */
function CreamActionPill({ onClick, icon: Icon, label, testid, active, disabled, spinning }) {
  const bg = active ? "rgba(29,158,117,0.10)" : GOLD_BG;
  const border = active ? "rgba(29,158,117,0.30)" : GOLD_BORDER;
  const color = active ? "#1d9e75" : "#8a6d00";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px",
        background: bg, border: `1px solid ${border}`,
        borderRadius: 999, color,
        fontSize: 12, fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        whiteSpace: "nowrap",
        fontFamily: "inherit",
        transition: "all 180ms ease",
      }}
    >
      <Icon size={13} weight={spinning ? "bold" : "regular"} className={spinning ? "animate-spin" : undefined} /> {label}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Divider — arany elválasztó (Landing Sales-ből)
   ═══════════════════════════════════════════════════════════════════════ */
function Divider() {
  return (
    <div style={{ height: 1, background: `linear-gradient(90deg, ${DIVIDER_START}, ${DIVIDER_END})`, margin: "44px 0 38px 0" }} aria-hidden="true" />
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   SectionActions / IconAction — pixelpontosan a Landing Sales-ből
   ═══════════════════════════════════════════════════════════════════════ */
function SectionActions({ onCopy, onEdit, editActive, testidPrefix }) {
  return (
    <div className="flex items-center gap-3" style={{ marginTop: 2 }}>
      <IconAction onClick={onCopy} icon={<Copy size={14} weight="regular" />} title="Másolás" testid={`${testidPrefix}-copy`} />
      <IconAction onClick={onEdit} icon={<PencilSimple size={14} weight={editActive ? "fill" : "regular"} />} title="Szerkesztés" testid={`${testidPrefix}-edit`} active={editActive} />
    </div>
  );
}
function IconAction({ onClick, icon, title, testid, active, disabled }) {
  return (
    <button
      type="button" onClick={onClick} title={title} disabled={disabled}
      data-testid={testid}
      className="inline-flex items-center transition"
      style={{
        background: "transparent", border: "none", padding: 4,
        color: active ? GOLD_DEEP : "#e2cf87",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.color = GOLD_DEEP; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.color = active ? GOLD_DEEP : "#e2cf87"; }}
    >
      {icon}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   SectionHeader — pixelpontosan a Landing Sales-ből
   ═══════════════════════════════════════════════════════════════════════ */
function SectionHeader({ title, children }) {
  return (
    <header className="flex items-start justify-between flex-wrap gap-4">
      <h2 className="font-heading"
        style={{ fontSize: 19, color: TEXT_DARK, fontWeight: 500, lineHeight: 1.4, letterSpacing: "-0.01em", flex: 1, minWidth: 0 }}>
        {title}
      </h2>
      {children}
    </header>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   FormattedText — determinisztikus szövegformázás a LINE/NOTE tartalmakhoz.

   Három vizuális kiemelés:
   1. `[...]`         → élőben kitöltendő placeholder — félkövér, kiemelt
                        arany háttérrel.
   2. `**...**`       → félkövér kiemelés (bold) — kulcsszavakhoz,
                        győzelmekhez, árakhoz, dátumokhoz.
   3. `(PÉLDA: ...)` → coachnak szóló konkretizálási példa — halvány,
                        dőlt, kisebb szöveg.
   ═══════════════════════════════════════════════════════════════════════ */
const _FORMAT_RE = /(\[[^\]\n]+\]|\*\*[^*\n]+\*\*|\(\s*PÉLDA\s*:[^)\n]*\))/g;

function FormattedText({ text }) {
  if (!text || typeof text !== "string") return null;
  const parts = String(text).split(_FORMAT_RE);
  return (
    <>
      {parts.map((chunk, i) => {
        if (!chunk) return null;
        // Élő placeholder: [XXX]
        if (chunk.startsWith("[") && chunk.endsWith("]")) {
          return (
            <span
              key={i}
              data-testid="sales-intro-call-placeholder-live"
              style={{
                display: "inline-block",
                padding: "1px 8px",
                borderRadius: 6,
                background: "rgba(234,179,8,0.16)",
                border: "1px solid rgba(234,179,8,0.40)",
                color: "#5b4600",
                fontWeight: 700,
                fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
            >
              {chunk}
            </span>
          );
        }
        // Coach-példa: (PÉLDA: ...) — jól olvasható, dőlt, halványabb
        if (/^\(\s*PÉLDA\s*:/i.test(chunk)) {
          return (
            <span
              key={i}
              data-testid="sales-intro-call-placeholder-example"
              style={{
                fontSize: "0.95em",
                fontStyle: "italic",
                color: "#8a7548",
                fontFamily: "inherit",
              }}
            >
              {chunk}
            </span>
          );
        }
        // Félkövér kiemelés: **...**
        if (chunk.startsWith("**") && chunk.endsWith("**") && chunk.length >= 4) {
          return (
            <strong
              key={i}
              data-testid="sales-intro-call-bold"
              style={{ fontWeight: 700, color: "inherit" }}
            >
              {chunk.slice(2, -2)}
            </strong>
          );
        }
        // Normál szöveg — sortöréseket megtartjuk
        return <React.Fragment key={i}>{chunk}</React.Fragment>;
      })}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   EditableInline — non-editing módban FormattedText render (élő
   placeholder + PÉLDA formázás). Editing módban változatlan raw
   contentEditable.
   ═══════════════════════════════════════════════════════════════════════ */
function EditableInline({ value, editing, onChange, style, multiline, testid, className, formatted }) {
  const ref = useRef(null);
  useEffect(() => {
    if (editing && ref.current && ref.current.innerText !== value) {
      ref.current.innerText = value || "";
    }
  }, [editing, value]);
  if (!editing) {
    return (
      <div className={className} style={style} data-testid={testid}>
        {formatted ? <FormattedText text={value} /> : value}
      </div>
    );
  }
  return (
    <div ref={ref} contentEditable suppressContentEditableWarning role="textbox"
      onBlur={(e) => onChange(e.currentTarget.innerText.replace(/\u00A0/g, " "))}
      data-testid={testid}
      className={className}
      style={{
        ...style, outline: "none",
        background: "rgba(234,179,8,0.06)",
        border: `1px dashed ${DIVIDER_START}`,
        borderRadius: 6, padding: "4px 8px",
        minHeight: multiline ? 32 : 24,
        whiteSpace: "pre-wrap",
      }}>
      {value}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   PhaseBlock — a 14 SalesXxxSection helyett EGY közös komponens
   15 fázishoz. LINE / NOTE blokk renderer + szekciónkénti Copy/Edit.
   ═══════════════════════════════════════════════════════════════════════ */

function PhaseBlock({ phase, onChange }) {
  const [editing, setEditing] = useState(false);
  const p = phase || {};
  const number = String(p.number ?? "").padStart(2, "0");
  const testidPrefix = `sales-intro-call-phase-${p.id || "unknown"}`;

  const copySection = async () => {
    const lines = [];
    const header = `${number} · ${p.title || ""}${p.duration ? ` · ${p.duration}` : ""}`;
    lines.push(header);
    lines.push("─".repeat(header.length));
    for (const b of (p.blocks || [])) {
      if (b.type === "line") lines.push(b.text || "");
      else if (b.type === "note") { lines.push(""); lines.push("[INSTRUKCIÓ]"); lines.push(b.text || ""); }
      lines.push("");
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n").trim() + "\n");
      toast.success(`${p.title || "Fázis"} vágólapra másolva`);
    } catch { toast.error("Nem sikerült a másolás"); }
  };

  const patchBlock = (bi, patch) => {
    const nextBlocks = (p.blocks || []).map((b, i) => (i === bi ? { ...b, ...patch } : b));
    onChange({ ...p, blocks: nextBlocks });
  };

  const blocks = Array.isArray(p.blocks) ? p.blocks : [];

  return (
    <section
      data-testid={testidPrefix}
      style={{ display: "flex", flexDirection: "column", gap: 18 }}
    >
      <SectionHeader
        title={
          <span className="flex items-baseline flex-wrap" style={{ gap: 10 }}>
            <span className="font-heading" style={{
              fontSize: 13, fontWeight: 600, color: GOLD_DEEP,
              letterSpacing: "0.02em",
            }}>
              {number}
            </span>
            <span>{p.title || ""}</span>
            {p.duration && (
              <span style={{
                fontSize: 12.5, color: GOLD_LABEL, fontStyle: "italic",
                fontWeight: 400, letterSpacing: 0, marginLeft: 2,
              }}>
                · {p.duration}
              </span>
            )}
          </span>
        }
      >
        <SectionActions
          onCopy={copySection}
          onEdit={() => setEditing((e) => !e)}
          editActive={editing}
          testidPrefix={testidPrefix}
        />
      </SectionHeader>

      {blocks.length === 0 ? null : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {blocks.map((b, bi) => (
            b.type === "line" ? (
              <LineBlock
                key={bi}
                text={b.text || ""}
                editing={editing}
                onChange={(nv) => patchBlock(bi, { text: nv })}
                testid={`${testidPrefix}-line-${bi}`}
              />
            ) : (
              <NoteBlock
                key={bi}
                text={b.text || ""}
                editing={editing}
                onChange={(nv) => patchBlock(bi, { text: nv })}
                testid={`${testidPrefix}-note-${bi}`}
              />
            )
          ))}
        </div>
      )}
    </section>
  );
}

/* LINE — bal oldali vékony arany szegély, Inter Regular, idézetjellegű.
   FormattedText: [élő placeholder] félkövér arany + (PÉLDA: ...) dőlt halvány. */
function LineBlock({ text, editing, onChange, testid }) {
  return (
    <div
      style={{
        borderLeft: `2px solid ${GOLD_PRIMARY}`,
        paddingLeft: 16,
      }}
    >
      <EditableInline
        value={text}
        editing={editing}
        multiline
        formatted
        onChange={onChange}
        testid={testid}
        style={{
          fontSize: 15.5, lineHeight: 1.7, color: TEXT_BODY,
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      />
    </div>
  );
}

/* NOTE — arany-krém kiemelődoboz. Alapesetben „INSTRUKCIÓ" pill.
   Ha a szöveg `PÉLDA:` prefixszel kezdődik, „PÉLDA" pillt mutat, a
   read-only nézetből a prefix elrejtve; editing módban a teljes raw
   szöveg szerkeszthető (a prefix marad, hogy ne vesszen el a jelzés). */
function NoteBlock({ text, editing, onChange, testid }) {
  const raw = String(text || "");
  const peldaMatch = /^\s*PÉLDA\s*:\s*/i.exec(raw);
  const isPelda = !!peldaMatch;
  const readonlyText = isPelda ? raw.slice(peldaMatch[0].length) : raw;
  return (
    <div
      style={{
        padding: "14px 18px 16px", borderRadius: 10,
        background: "#fdf3d6", border: "1px solid #f0dfa8",
      }}
      data-testid={isPelda ? `${testid}-pelda` : testid}
    >
      <div style={{
        display: "inline-block", marginBottom: 8,
        fontSize: 11, fontWeight: 600, color: GOLD_DEEP,
        letterSpacing: "0.12em", textTransform: "uppercase",
        fontFamily: "Inter, system-ui, sans-serif",
      }}>
        {isPelda ? "PÉLDA" : "INSTRUKCIÓ"}
      </div>
      {editing ? (
        <EditableInline
          value={raw}
          editing
          multiline
          formatted
          onChange={onChange}
          testid={testid}
          style={{
            fontSize: 13.5, lineHeight: 1.65, color: TEXT_MID,
            fontStyle: isPelda ? "normal" : "italic",
            fontFamily: "Inter, system-ui, sans-serif",
          }}
        />
      ) : (
        <div
          data-testid={testid}
          style={{
            fontSize: 13.5, lineHeight: 1.65, color: TEXT_MID,
            fontStyle: isPelda ? "normal" : "italic",
            fontFamily: "Inter, system-ui, sans-serif",
          }}
        >
          <FormattedText text={readonlyText} />
        </div>
      )}
    </div>
  );
}
